import { Button } from '@/components/ui/button';
import { trpc, NoteOrigin } from 'src/trpc';
import { Label } from '@/components/ui/label';
import { formatDateTime } from '@libs/frontend-services';
import { FC, useMemo, useState } from 'react';
import { Textarea } from '@/components/ui/textarea';

export const Notes: FC<{ entityId: string; origin: NoteOrigin }> = ({ entityId, origin }) => {
  const [noteContent, setNoteContent] = useState('');
  const utils = trpc.useUtils();
  const { data } = trpc.admin.note.getNotes.useQuery({ entityId, origin });
  const { mutate } = trpc.admin.note.addNote.useMutation({
    onSuccess: () => {
      utils.admin.note.getNotes.invalidate();
    },
  });

  // Notes only store the author id, so resolve them to names the same way ActivityLog does.
  const authorIds = useMemo(
    () => Array.from(new Set((data?.notes || []).map((note) => note.createdBy).filter(Boolean))),
    [data],
  );

  const { data: authors = [] } = trpc.admin.user.getUsersByIds.useQuery(authorIds, {
    enabled: authorIds.length > 0,
  });

  const authorNameMap = useMemo(() => {
    const map = new Map<string, string>();
    authors.forEach((user) => {
      if (user && user._id) {
        const fullName = `${user.name?.first || ''} ${user.name?.last || ''}`.trim();
        map.set(user._id.toString(), fullName || user.email || 'Unknown User');
      }
    });
    return map;
  }, [authors]);

  return (
    <>
      <div className="flex flex-col gap-4 bg-white p-4 w-full rounded-md">
        <div className="flex flex-col">
          <Label className="label mb-2 text-sm">Notes</Label>
          <Textarea
            placeholder="Leave a comment..."
            className="placeholder:text-text-light text-text-light"
            onChange={(e) => setNoteContent(e.target.value)}
            value={noteContent}
          />
        </div>
        <div className="flex flex-row justify-end">
          <Button
            onClick={() => {
              mutate({
                content: noteContent,
                entityId,
                origin,
              });
              setNoteContent('');
            }}
            className="text-white"
            disabled={!noteContent}
          >
            Post
          </Button>
        </div>
      </div>
      <div className="px-2 ">
        <div className="flex flex-col relative gap-6 border-l-2 border-slate-100 pt-6 pl-3">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
          {data?.notes.map((note: any) => (
            <div className="flex flex-row items-center justify-between" key={note._id}>
              <div className="absolute -left-[5px] w-[8px] h-[8px] bg-slate-300 rounded-full"></div>
              <div className="flex justify-between items-center w-full">
                <div className="text-xs flex-1 text-slate-500 mr-2">
                  <p>{note.content}</p>
                  {note.createdBy && (
                    <p className="text-slate-400">
                      {authorNameMap.get(note.createdBy) || 'Unknown User'}
                    </p>
                  )}
                </div>
                <div className="text-xs text-right text-slate-500">
                  <p>{formatDateTime(note.createdAt, "MMM d, yyyy 'at' p")}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
